import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder } from '@angular/forms';
import { EclassWorkerEnum } from '@app/core/models/eclass-worker.enum';

import { SHARED_MODULES } from '@app/shared';
import { Checker, RequiredAccessArray } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { startOfDay } from 'date-fns';
import { isNil } from 'lodash';
import { combineLatest, forkJoin, map, Observable, of, take } from 'rxjs';
import { distinctUntilChanged, switchMap } from 'rxjs/operators';
import { FilterService } from '../../services/filter.service';
import { CalendarPostComponent } from '../calendar-post/calendar-post.component';
import { ActivityHeader } from '../post-activity/activity';
import { AnnouncementHeader } from '../post-announcement/announcement';
import { DailyHeader } from '../post-daily/daily';
import { CPBProvaHeader } from '../post-exam/cpbprova';
import { InboxHeader } from '../post-inbox/inbox';
import { Builder, Shortcut } from '../shortcut-list/shortcut';

interface Item extends Builder {
  [k: string]: any;
}
interface Menu {
  folders: Item[];
  modules: Item[];
}
@Component({
  imports: [SHARED_MODULES, CalendarPostComponent],
  templateUrl: './sidenav-filter-posts.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidenavFilterPostsComponent implements OnInit {
  protected items$: Observable<Menu>;
  protected readonly worker = EclassWorkerEnum;
  protected readonly folders: Item[] = [
    () => ({
      title: InboxHeader.plural,
      icon: InboxHeader.icon,
      worker: InboxHeader.worker,
      style: 'hover:bg-modules-student-200 hover:text-modules-student-500 current:bg-modules-student-500',
      isCurrent: (worker) => !worker,
    }),
    () => ({
      title: DailyHeader.plural,
      icon: DailyHeader.icon,
      worker: DailyHeader.worker,
      style: 'hover:bg-modules-eclass-200 hover:text-modules-eclass-700 current:bg-modules-eclass-500',
      isCurrent: (worker) => worker === DailyHeader.worker,
    }),
    () => ({
      title: ActivityHeader.plural,
      icon: ActivityHeader.icon,
      worker: ActivityHeader.worker,
      style: 'hover:bg-mantis-200 hover:text-mantis-600 current:bg-base-post-activity',
      isCurrent: (worker) => worker === ActivityHeader.worker,
    }),
    () => ({
      title: CPBProvaHeader.plural,
      icon: CPBProvaHeader.icon,
      worker: CPBProvaHeader.worker,
      style:
        'hover:bg-modules-assessments-manager-200 hover:text-modules-assessments-manager-500  current:bg-modules-assessments-manager-500 ',

      isCurrent: (worker) => worker === CPBProvaHeader.worker,
      permission: [1, 4],
      show: this._store
        .select(AppSelectors.DefaultFunction)
        .pipe(map((defaultFunction) => Checker.isStudentOrResponsible(defaultFunction))),
    }),
    () => ({
      title: AnnouncementHeader.plural,
      icon: AnnouncementHeader.icon,
      worker: AnnouncementHeader.worker,
      style: 'hover:bg-modules-school-200 hover:text-modules-school-500 current:bg-modules-school-500 ',
      isCurrent: (worker) => worker === AnnouncementHeader.worker,
    }),
  ];

  protected readonly modules: Item[] = [
    // () => ({
    //   title: 'eclass.agenda.filtros.sequecia-didatica',
    //   icon: 'icon-seq-didatica',
    //   style: 'hover:bg-modules-sequence-200 hover:text-modules-sequence-600 current:bg-modules-sequence-500 ',
    //   worker: EclassWorkerEnum.Sequence,
    //   isCurrent: (worker) => worker === EclassWorkerEnum.Sequence,
    //   permission: [1, 1],
    // }),
    () => ({
      title: 'eclass.agenda.filtros.aula-online',
      icon: 'icon-lives',
      style: 'hover:bg-base-zoom-200 hover:text-base-zoom-500 current:bg-base-zoom-500 ',
      worker: EclassWorkerEnum.Live,
      isCurrent: (worker) => worker === EclassWorkerEnum.Live,
      permission: [1000, 1000],
    }),
  ];

  protected readonly filterService = inject(FilterService);
  protected workerId = signal<EclassWorkerEnum>(EclassWorkerEnum.All);
  private readonly _destroy = inject(DestroyRef);
  private readonly _store = inject<Store<AppState>>(Store);

  public ngOnInit() {
    this.filterService.filter$
      .pipe(takeUntilDestroyed(this._destroy), distinctUntilChanged())
      .subscribe(({ worker_id }) => this.workerId.set(worker_id));

    this.load();
  }

  public onFilter(worker?: EclassWorkerEnum) {
    this.filterService.sidenav((value) => ({ ...value, worker_id: worker }));
  }

  private load() {
    this.items$ = this._store.select(AppSelectors.appFeature).pipe(
      takeUntilDestroyed(this._destroy),
      switchMap(() =>
        forkJoin({
          folders: combineLatest(this.folders.map((item) => this.resolve(item()))),
          modules: combineLatest(this.modules.map((item) => this.resolve(item()))),
        }),
      ),
      map(({ folders, modules }) => ({
        folders: folders.filter(({ show, permission }) => show && permission),
        modules: modules.filter(({ show, permission }) => show && permission),
      })),
    );
  }

  private resolve(item: Shortcut): Observable<any> {
    return combineLatest({
      show: this.helperShow(item.show),
      permission: this.helperAllow(item.permission),
      item: of(item),
    }).pipe(
      take(1),
      map(({ show, permission, item }) => ({ ...item, show, permission })),
    );
  }

  private helperShow(show: Observable<boolean>): Observable<boolean> {
    if (isNil(show)) {
      return of(true);
    }
    return show;
  }

  private helperAllow(permission: RequiredAccessArray): Observable<boolean> {
    if (isNil(permission)) {
      return of(true);
    }
    return this._store.select(AppSelectors.isAllow(permission));
  }
}
